"use client";

import { cn } from "@/lib/utils";
import { useEventConnect } from "@/store/eventConnect";
import { currency } from "@yn/money";
import { useCallback, useEffect, useRef, useState } from "react";

// 列表项数据类型
interface ListItem<T = any> {
    id: string;
    data: T;
    x: number; // 当前 X 坐标
    y: number; // 当前 Y 坐标
    startY: number; // 起始 Y 坐标（用于缓动计算）
    targetY: number; // 目标 Y 坐标
    progress: number; // 动画进度 (0-1)
    opacity: number; // 透明度（用于淡入效果）
    isDestroying?: boolean; // 是否正在销毁
    destroyStartY?: number; // 销毁开始时的 Y 坐标
}

// ease-in 缓动函数：先慢后快
const easeIn = ( t: number ): number => t * t;
// ease-out 缓动函数：先快后慢
const easeOut = ( t: number ): number => t * ( 2 - t );

interface UseCanvasListAnimationOptions<T> {
    canvasWidth: number;
    canvasHeight: number;
    itemHeight: number; // 每个子项的高度
    animationSpeed?: number; // 动画速度 (0-1)，默认 0.1
    destroyAnimationSpeed?: number; // 销毁动画速度 (0-1)，默认 0.2
    maxItems?: number; // 最多显示的子项数量 默认5
    autoDestroyTime?: number; // 自动销毁时间（毫秒），可选
    bottomOffset?: number; // 底部偏移量（如时间轴高度），默认 0
    renderItem: ( ctx: CanvasRenderingContext2D, item: ListItem<T>, index: number, width: number ) => void;
}

function useCanvasListAnimation<T>( options: UseCanvasListAnimationOptions<T> ) {
    const {
        canvasWidth,
        canvasHeight,
        itemHeight,
        animationSpeed = 0.1,
        destroyAnimationSpeed = 0.2,
        maxItems = 5,
        autoDestroyTime,
        bottomOffset = 0,
        renderItem,
    } = options;

    const canvasRef = useRef<HTMLCanvasElement>( null );
    const itemsRef = useRef<ListItem<T>[]>( [] );

    const animationFrameRef = useRef<number>( 0 );
    const sizeRef = useRef( { width: canvasWidth, height: canvasHeight } );

    // 更新尺寸引用
    useEffect( () => {
        sizeRef.current = { width: canvasWidth, height: canvasHeight };
    }, [ canvasWidth, canvasHeight ] );

    // 添加新数据
    const addItem = useCallback( ( id: string, data: T ) => {
        const items = itemsRef.current;
        const { height } = sizeRef.current;

        // 计算当前活跃的项数量，若加入新项后超限，则需销毁最早的活跃项
        const activeItemsCount = items.filter( ( item ) => !item.isDestroying ).length;
        const countToDestroy = Math.max( 0, activeItemsCount - maxItems + 1 );
        let destroyedCount = 0;

        // 将所有现有项的目标位置向上移动一个 itemHeight，并重置动画进度
        for ( const item of items ) {
            item.startY = item.y; // 从当前位置开始新的动画
            const nextTargetY = item.targetY - itemHeight;
            item.progress = 0; // 重置进度

            // 如果已经处于销毁状态，限制移动距离最多为 2 个 itemHeight
            if ( item.isDestroying && item.destroyStartY !== undefined ) {
                const limitY = item.destroyStartY - 2 * itemHeight;
                item.targetY = Math.max( nextTargetY, limitY );
            } else {
                item.targetY = nextTargetY;
            }

            // 如果需要销毁，且当前项是活跃的
            if ( destroyedCount < countToDestroy && !item.isDestroying ) {
                item.isDestroying = true;
                item.destroyStartY = item.y; // 记录销毁开始的位置
                destroyedCount++;
            }
        }

        // 新项从底部开始，初始位置在底部偏移量上方
        const effectiveBottom = height - bottomOffset; // 有效底部位置（扣除时间轴等）
        const newItem: ListItem<T> = {
            id,
            data,
            x: 16, // 默认 X 坐标
            y: effectiveBottom, // 从有效底部开始
            startY: effectiveBottom, // 起始位置
            targetY: effectiveBottom - itemHeight, // 目标位置在有效底部上方
            progress: 0, // 动画进度
            opacity: 0, // 初始透明，淡入效果
        };

        items.push( newItem );

        // 自动销毁逻辑
        if ( autoDestroyTime && autoDestroyTime > 0 ) {
            setTimeout( () => {
                const currentItems = itemsRef.current;
                const targetItem = currentItems.find( ( i ) => i.id === id );

                if ( targetItem && !targetItem.isDestroying ) {
                    targetItem.startY = targetItem.y;
                    targetItem.targetY -= itemHeight;
                    targetItem.progress = 0;
                    targetItem.isDestroying = true;
                    targetItem.destroyStartY = targetItem.y; // 记录销毁开始的位置
                }
            }, autoDestroyTime );
        }
    }, [ itemHeight, maxItems, autoDestroyTime ] );

    // 批量添加数据
    const addItems = useCallback( ( newItems: { id: string; data: T }[] ) => {
        for ( const item of newItems ) {
            addItem( item.id, item.data );
        }
    }, [ addItem ] );

    // 清空所有数据
    const clearItems = useCallback( () => {
        itemsRef.current = [];
    }, [] );

    // 动画循环
    useEffect( () => {
        const canvas = canvasRef.current;
        if ( !canvas ) return;

        const ctx = canvas.getContext( "2d" );
        if ( !ctx ) return;

        // 设置 canvas 的实际像素尺寸（支持高清屏）
        const dpr = window.devicePixelRatio || 1;
        canvas.width = canvasWidth * dpr;
        canvas.height = canvasHeight * dpr;
        ctx.scale( dpr, dpr );

        const animate = () => {
            // 清空画布
            ctx.clearRect( 0, 0, canvasWidth, canvasHeight );

            const items = itemsRef.current;

            // 更新每个项的位置和透明度
            for ( const item of items ) {
                // 检测销毁逻辑：每次渲染检测 targetY 位置
                if ( !item.isDestroying && item.targetY < 0 ) {
                    item.isDestroying = true;
                    item.destroyStartY = item.y; // 记录销毁开始的位置
                }

                // 根据状态选择速度
                const currentSpeed = item.isDestroying ? destroyAnimationSpeed : animationSpeed;

                // 更新动画进度
                if ( item.progress < 1 ) {
                    item.progress = Math.min( 1, item.progress + currentSpeed );
                }

                // 根据状态选择缓动函数
                // 正常：ease-in (先慢后快)
                // 销毁：ease-out (先快后慢)
                const easedProgress = item.isDestroying ? easeOut( item.progress ) : easeIn( item.progress );

                item.y = item.startY + ( item.targetY - item.startY ) * easedProgress;

                // 透明度处理
                if ( item.isDestroying ) {
                    // 销毁逻辑：渐隐，从当前透明度递减
                    item.opacity = Math.max( 0, item.opacity - currentSpeed );
                } else if ( item.opacity < 1 ) {
                    // 进场逻辑：淡入
                    item.opacity = Math.min( 1, item.opacity + 0.05 );
                }
            }

            // 过滤掉完全销毁的项（标记为销毁且透明度为0）
            itemsRef.current = items.filter( ( item ) => !( item.isDestroying && item.opacity <= 0 ) );

            // 渲染每个项
            const visibleItems = itemsRef.current;
            for ( let i = 0; i < visibleItems.length; i++ ) {
                const item = visibleItems[i];
                // 只渲染还在画布范围内或者还在渐隐过程中的
                if ( item.opacity > 0 ) {
                    ctx.save();
                    ctx.globalAlpha = item.opacity;
                    renderItem( ctx, item, i, canvasWidth );
                    ctx.restore();
                }
            }

            animationFrameRef.current = requestAnimationFrame( animate );
        };

        animationFrameRef.current = requestAnimationFrame( animate );

        return () => {
            if ( animationFrameRef.current ) {
                cancelAnimationFrame( animationFrameRef.current );
            }
        };
    }, [ canvasWidth, canvasHeight, itemHeight, animationSpeed, renderItem ] );

    return { canvasRef, addItem, addItems, clearItems, getItems: () => itemsRef.current };
}

// Demo: 交易实时数据展示组件
interface TradeData {
    type: 1 | 2;
    price: string;
    amount: string;
    time: string;
}

function TradeLive( props: { className?: string } ) {
    const { className } = props;
    const containerRef = useRef<HTMLDivElement>( null );
    const [ size, setSize ] = useState( { width: 0, height: 0 } );
    const onTradeAmountChange = useEventConnect( state => state.onTradeAmountChange );
    const pendingTradesRef = useRef<{ id: string; data: TradeData }[]>( [] );
    const timerRef = useRef<NodeJS.Timeout | null>( null );
    const addItemsRef = useRef<any>( null );
    const resizeFrameRef = useRef<number | null>( null );

    const ITEM_HEIGHT = 2.0 * 10; // 18px = 18px; 14px text + ~4px gap

    // 监听父容器尺寸变化
    useEffect( () => {
        const container = containerRef.current;
        if ( !container ) return;

        const resizeObserver = new ResizeObserver( ( entries ) => {
            for ( const entry of entries ) {
                const { width, height } = entry.contentRect;
                if ( resizeFrameRef.current !== null ) {
                    cancelAnimationFrame( resizeFrameRef.current );
                }
                resizeFrameRef.current = requestAnimationFrame( () => {
                    resizeFrameRef.current = null;
                    setSize( (
                        prev,
                    ) => ( prev.width === width && prev.height === height ? prev : { width, height } ) );
                } );
            }
        } );

        resizeObserver.observe( container );
        return () => {
            resizeObserver.disconnect();
            if ( resizeFrameRef.current !== null ) {
                cancelAnimationFrame( resizeFrameRef.current );
                resizeFrameRef.current = null;
            }
        };
    }, [] );

    // 渲染单个交易项
    const renderTradeItem = useCallback(
        ( ctx: CanvasRenderingContext2D, item: ListItem<TradeData>, _index: number, _width: number ) => {
            const { data, x, y } = item;

            // 根据 type 设置颜色
            ctx.fillStyle = data.type === 1 ? "#30A159" : "#E23939";
            ctx.font = "14px system-ui";
            ctx.fillText( `+${currency + data.price}`, x, y + ITEM_HEIGHT / 2 + 5 );
            ctx.textAlign = "left";
        },
        [],
    );

    const { canvasRef, addItems } = useCanvasListAnimation<TradeData>( {
        canvasWidth: size.width,
        canvasHeight: size.height,
        itemHeight: ITEM_HEIGHT,
        animationSpeed: 0.12,
        destroyAnimationSpeed: 0.04,
        maxItems: 5,
        autoDestroyTime: 3000,
        bottomOffset: 30, // 时间轴高度约 30px
        renderItem: renderTradeItem,
    } );

    // 保存 addItems 引用
    useEffect( () => {
        addItemsRef.current = addItems;
    }, [ addItems ] );

    // 监听实时交易数据
    useEffect( () => {
        // 处理待处理的交易数据
        const processPendingTrades = () => {
            if ( pendingTradesRef.current.length > 0 && addItemsRef.current ) {
                addItemsRef.current( pendingTradesRef.current );
                pendingTradesRef.current = [];
            }
            timerRef.current = null;
        };

        // 监听交易金额变化
        const unsubscribe = onTradeAmountChange( ( event ) => {
            const tradeData: TradeData = {
                type: event.guessType, // 可根据业务逻辑调整
                price: event.tradeAmount.toFixed( 2 ),
                amount: event.tradeAmount.toFixed( 4 ),
                time: new Date( event.timestamp ).toLocaleTimeString(),
            };

            // 添加到待处理队列
            pendingTradesRef.current.push( { id: `trade-${event.guessNo}-${event.timestamp}`, data: tradeData } );

            // 如果还没有计时器，创建一个
            if ( !timerRef.current ) {
                timerRef.current = setTimeout( processPendingTrades, 500 );
            }
        } );

        return () => {
            unsubscribe();
            if ( timerRef.current ) {
                clearTimeout( timerRef.current );
                timerRef.current = null;
            }
        };
    }, [ onTradeAmountChange ] );

    return (
        <div ref={containerRef} className={cn( className )}>
            <canvas ref={canvasRef} className="size-full" />
        </div>
    );
}

export default TradeLive;
